import {
	Component,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
	forwardRef,
	TrackByFunction,
	input,
	TemplateRef,
	signal,
	computed,
	linkedSignal,
	resource,
	output,
	effect,
	untracked,
} from '@angular/core'
import {
	ControlValueAccessor,
	FormControl,
	NG_VALUE_ACCESSOR,
} from '@angular/forms'
import type {
	BlockAppearance,
	SearchableDropdownBindingMode,
	WritableSignal,
} from '@lg/shared'
import { DataQueryFilter } from '@lg/shared'
import {
	type EntityClassOnDb,
	type FindManyOptions,
	type ServerDataStoreClass,
} from '@lg/shared'
import {
	switchMap,
	Subscription,
	debounceTime,
	startWith,
	distinctUntilChanged,
	shareReplay,
	from,
} from 'rxjs'
import { toObservable, toSignal } from '@angular/core/rxjs-interop'

interface Option<T extends { id: string | number }> {
	id: T['id']
	value: T
	/** @deprecated is this needed? */
	index?: number
	name: string
}

@Component({
	selector: 'lowgile-searchable-dropdown',
	templateUrl: './searchable-dropdown.component.html',
	styleUrls: ['./searchable-dropdown.component.scss'],
	encapsulation: ViewEncapsulation.None,
	standalone: false,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => SearchableDropdownComponent),
			multi: true,
		},
	],
})
export class SearchableDropdownComponent<T extends { id: string | number }>
	implements OnInit, OnDestroy, ControlValueAccessor
{
	debouncingDelayMs = input<number>(250)
	selectedOption = signal<Option<T> | null>(null)
	protected text = linkedSignal(() => this.selectedOption()?.name ?? '')
	protected textDebounced = toSignal(
		toObservable(this.text).pipe(
			debounceTime(this.debouncingDelayMs()),
			distinctUntilChanged(),
			startWith(''),
			shareReplay(),
		),
	)
	protected text$ = toObservable(this.text)

	dataFn = input<(query: string) => T[] | Promise<T[]>>(undefined)
	dataInput = input<T[] | Promise<T[]>>(undefined, { alias: 'data' })
	dataInputAwaited = toSignal(
		toObservable(this.dataInput).pipe(
			switchMap((data) => from(Promise.resolve(data))),
			distinctUntilChanged(),
			shareReplay(),
		),
	)
	dataFetch = resource({
		params: () => ({
			text: this.textDebounced(),
			dataFn: this.dataFn(),
			dataInput: this.dataInputAwaited(),
			entity: this.entity(),
			serverDataStore: this.serverDataStore(),
			findOptions: this.findOptions(),
			maxResults: this.maxResults(),
			allClasses: this.allClasses(),
			displayWith: this.displayWith(),
			minSearchLength: this.minSearchLength(),
			emptySelection: this.emptySelection(),
		}),
		loader: async ({ params }) => {
			if (params.text.length < params.minSearchLength) {
				if (this.selectedOption()) return [this.selectedOption()?.value] // does not need to be reactive, since it is only used for initial value
				return []
			}

			let data: T[] = []
			if (params.dataFn) {
				data = await params.dataFn(params.text)
			} else if (params.serverDataStore) {
				if (params.dataInput)
					throw new Error(
						'dataInput and serverDataStore source are not compatible',
					)

				data = await params.serverDataStore.loadByQuery<T>(
					params.entity,
					{
						query: `"${params.text}"`,
					},
					{
						...params.findOptions,
						take: (params.maxResults || params.findOptions?.take) ?? 0,
					},
				)
			} else {
				if (!params.dataInput)
					throw new Error(
						'dataInput is required when serverDataStore is not provided',
					)

				if (!params.text) {
					data = params.dataInput
				} else {
					const filter = new DataQueryFilter(
						params.entity,
						{
							query: params.text,
						},
						params.allClasses,
					)
					const dataInput = await params.dataInput
					data = filter.filterList(dataInput) as T[]
				}
			}

			return data
		},
	})
	data = linkedSignal({
		source: () => () => ({
			status: this.dataFetch.status(),
			value: this.dataFetch.value(),
		}),
		computation: (source, previous) => {
			const { status, value } = source()

			if (status == 'loading') return previous?.value ?? []
			if (status == 'error') return []
			return value ?? []
		},
	}) as WritableSignal<T[]>
	protected options = computed(() => {
		const options = this.data().map((value) => this.toOption(value))

		if (this.emptySelection()) {
			options.unshift({
				id: this.emptySelection()[0],
				value: this.emptySelection()[0],
				name: this.emptySelection()[1],
			})
		}
		return options
	})

	label = input<string>('')
	bindingMode = input<SearchableDropdownBindingMode>('object')
	formControl = input<FormControl>()
	entity = input<EntityClassOnDb>()
	serverDataStore = input<ServerDataStoreClass>()
	minSearchLength = input<number>(3)
	maxResults? = input<number>(20)
	displayWith = input<
		(
			input: T | null,
			index: number | undefined,
			context: Record<string, any>,
		) => string
	>(() => '')
	displayWithContext = input<Record<string, any>>({})
	findOptions = input<FindManyOptions<any>>()
	trackBy = input.required<TrackByFunction<T>>()
	allowClearing = input<boolean>(true)
	allClasses = input<Record<string, Record<string, any>>>({})
	emptySelection = input<[any, string, boolean] | undefined>()
	itemTemplate = input<TemplateRef<any>>()
	appearance = input<BlockAppearance | undefined>()

	optionSelected = output<T | null>()

	protected onChanged?: (value: any) => void
	private subscriptions: Subscription[] = []
	private lastSelectedOption: Option<T> | null | undefined = undefined

	constructor() {
		effect(() => {
			const lastSelectedOption = this.lastSelectedOption
			this.lastSelectedOption = this.selectedOption()
			if (lastSelectedOption === undefined) return
			if (lastSelectedOption === this.lastSelectedOption) return

			this.onChanged?.(this.lastSelectedOption?.value ?? null)
			this.optionSelected.emit(this.lastSelectedOption?.value ?? null)
		})
	}

	ngOnInit(): void {
		this.updateInternalValue(this.formControl().value)

		this.subscriptions
			.push
			// this.formControl().valueChanges.subscribe((id) =>
			// 	this.updateInternalValue(id),
			// ),

			/* 			combineLatest([
				this.text$.pipe(
					map((text) => (text ?? '').trim()),
					debounceTime(this.debouncingDelayMs()),
					distinctUntilChanged(),
					startWith(''),
				),
				this.options$.pipe(
					distinctUntilChanged(),
					startWith([] as T[]),
				),
			])
				.pipe(switchMap(([text, _data]) => this.executeSearch(text)))
				.subscribe((searchResults) => {
					const list = searchResults.map((result, idx) => ({
						id: result.id,
						value: result,
						name: this.displayWith(result, idx, this.displayWithContext()),
					}))

					const emptySelection = this.emptySelection()
					if (emptySelection) {
						list.unshift({
							id: emptySelection[0],
							value: emptySelection[0],
							name: emptySelection[1],
						})
					}
					this.options.set(list)
					// this.cdRef.markForCheck()
				}), */
			()
	}

	ngOnDestroy() {
		this.subscriptions.forEach((s) => s.unsubscribe())
	}

	private toOption(value: T): Option<T> {
		return {
			id: value.id,
			value: value,
			name: this.displayWith()(
				value,
				0,
				untracked(() => this.displayWithContext()), //  TODO: find better solution. untracked was introduced to avoid unnecessary re-computations of computed signal "options" triggered through displayWithContext()
			),
		}
	}

	// onSelected(value: T | null) {
	// 	this.selectedOption = value

	// 	if (this.bindingMode == 'object') {
	// 		this.formControl.setValue(value)
	// 	} else {
	// 		this.formControl.setValue(value?.id)
	// 	}
	// 	// (this.formBinding.object as any)[this.formBinding.property] = this.value?.id
	// 	this.optionSelected.emit(this.selectedOption)
	// }

	writeValue(obj: any): void {
		this.updateInternalValue(obj)
	}

	registerOnChange(fn: any): void {
		this.onChanged = fn
	}

	registerOnTouched(_fn: any): void {
		// this.onTouched = fn
	}

	async updateInternalValue(objOrId: T | T['id']) {
		if (!objOrId) {
			this.selectedOption.set(null)
		} else if (typeof objOrId == 'object') {
			this.selectedOption.set(this.toOption(objOrId))
		} else {
			const foundObject = this.options().find((option) => option.id === objOrId)
			if (foundObject) {
				this.selectedOption.set(this.toOption(foundObject.value))
			} else {
				this.selectedOption.set(null)
			}
		}

		if (!this.selectedOption() && typeof objOrId != 'object') {
			const loadedObject = await this.serverDataStore().loadById<T>(
				this.entity(),
				objOrId,
			)
			if (loadedObject) {
				this.data.set([loadedObject])
				const option = this.options()[0]
				this.selectedOption.set(option)
				this.text.set(option.name)
			} else {
				this.data.set([])
				this.selectedOption.set(null)
				this.text.set('')
			}
		}
	}

	// private executeSearch(text: string | null) {
	// 	if (text == null || text.length < this.minSearchLength) return of([] as T[])

	// 	const dataPromise = Promise.resolve(this.dataInput())
	// 	const resultPromise = dataPromise.then((data) => {
	// 		if (data) {
	// 			if (this.entity) {
	// 				const filter = new DataQueryFilter(
	// 					this.entity,
	// 					{
	// 						query: text,
	// 					},
	// 					this.allClasses,
	// 				)
	// 				return filter.filterList(data)
	// 			}

	// 			const parts = text.toLocaleLowerCase().split(/\s+/g)
	// 			return data.filter((item) => {
	// 				const str = JSON.stringify(item).toLocaleLowerCase()
	// 				return parts.every((part) => str.includes(part))
	// 			})
	// 		}

	// 		const loadPromise = this.serverDataStore.loadByQuery<T>(
	// 			this.entity,
	// 			{
	// 				query: text,
	// 			},
	// 			{
	// 				...this.findOptions,
	// 				take: (this.maxResults || this.findOptions?.take) ?? 0,
	// 			},
	// 		)
	// 		return loadPromise
	// 	})

	// 	return from(resultPromise)
	// }

	// private findObjectWithId(id: T['id']): { value: T; index: number } | null {
	// 	if (!id) return null
	// 	if (this.selectedOption()?.id === id) return { value: this.selectedOption()?.value, index: this.index }

	// 	const index = this.options().findIndex((item) => item.id === id)
	// 	if (index >= 0) return { value: this.options[index], index }

	// 	return null
	// }
}
